import { useSubscriptionStore } from '../stores/subscription.store';

/**
 * Entitlement names that USED to plan-gate features but were dropped
 * in PR3 when those features moved to credits-only access. Calls to
 * useEntitlement for these names get a permissive result (no gate)
 * so legacy call sites stop firing the upgrade prompt unnecessarily.
 *
 * The actual server-side credit check (PR2's InsufficientCreditsException
 * → PR9's UpgradePromptModal) handles "user can't afford this feature"
 * — the frontend doesn't need a parallel guard for these.
 *
 * Real plan-gates after PR3:
 *   - questions_oral
 *   - cohort_management
 *   - progress_insights_premium (placeholder — feature not yet built)
 */
const DROPPED_ENTITLEMENTS = new Set([
  'games_full',
  'games_basic',
  'empy_conversation',
  'support_chat',
  'video_calls',
  'ai_tools',
  'on_demand_empathy_ai',
  'on_demand_human_empathy',
  'lessons_text',
  'tests',
]);

export function useEntitlement(feature: string) {
  const isDropped = DROPPED_ENTITLEMENTS.has(feature);

  const hasEntitlement = useSubscriptionStore((state) =>
    isDropped ? true : state.hasEntitlement(feature),
  );
  const isLoading = useSubscriptionStore((state) => state.isLoading);
  const isPremium = useSubscriptionStore((state) => state.isPremium());
  const isElite = useSubscriptionStore((state) => state.isElite());
  const isPaidPlan = useSubscriptionStore((state) => state.isPaidPlan());

  return {
    hasEntitlement,
    isLoading,
    showUpgrade: isDropped ? false : !hasEntitlement && !isLoading,
    isPremium,
    isElite,
    isPaidPlan,
  };
}
